import { useState, useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Button, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { Trash2Icon } from 'lucide-react';
import { useDetachProductsMutation } from '../hooks/useDetachProductsMutation';
import { AttachProductsModal } from './AttachProductsModal';
import type { ProductRef } from '../types/security-admin.types';

interface Props {
  groupId: string;
  ownerDealerId: string;
  products: ProductRef[];
}

export function SecurityGroupProductsPanel({ groupId, ownerDealerId, products }: Props) {
  const [showModal, setShowModal] = useState(false);
  const detachMutation = useDetachProductsMutation(groupId);

  const columns = useMemo<ColumnDef<ProductRef>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: (info) => {
        const product = info.row.original;
        // Icon-only Button — `Tooltip` is visual only, so the accessible
        // name comes from `ariaLabel`. Use the product name so screen-reader
        // users can tell rows apart.
        return (
          <div className="flex space-x-2">
            <PermissionGuard permission={{ resource: 'security_group', action: 'manage_products' }}>
              <Tooltip content="Detach product">
                <TextLink
                  onClick={() => detachMutation.mutate([product.id])}
                  color="error"
                  startIcon={<Trash2Icon size={18} />}
                  disabled={detachMutation.isPending}
                  ariaLabel={`Detach product ${product.name}`}
                />
              </Tooltip>
            </PermissionGuard>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [detachMutation.isPending]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Attached Products</h3>
        <PermissionGuard permission={{ resource: 'security_group', action: 'manage_products' }}>
          <Button size="sm" type="button" onClick={() => setShowModal(true)}>
            Attach Products
          </Button>
        </PermissionGuard>
      </div>

      {products.length === 0 ? (
        <p className="text-mp-muted text-sm">No products attached to this security group. Members reach every product at this group's dealers.</p>
      ) : (
        <Table<ProductRef> data={products} columns={columns} enableSorting={false} />
      )}

      {showModal && (
        <AttachProductsModal
          groupId={groupId}
          ownerDealerId={ownerDealerId}
          attachedProductIds={products.map((p) => p.id)}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
